import React, { useEffect, useContext, useState } from 'react'
import IAgreement, { IAgreementData, IFilteredData } from './agreements.interface'
import AgreementContext from './agreements-context'
import Spinner from './../spinner/spinner'
import DashboardContext from '../layout/dashboard-context'
import StatisticsSubmitHeader from '../statistics-submit-header/statistics-submit-header'
import AgreementForm from './agreements-form'
import BillingInfo from '../billing-info/billing-info'
import AgreementsTable from './agreements-table'
import UsersPage from '../user-management/user-page'
import { useSearchParams, useNavigate } from 'react-router-dom'
import api from '../../api'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown, faChevronUp, faFileDownload, faArrowRight } from '@fortawesome/free-solid-svg-icons'

const Agreement: React.FC<IAgreement> = () => {
  const { receivedData, setReceivedData } = useContext(AgreementContext)
  const { collectiveAgreements, setCollectiveAgreements } = useContext(AgreementContext)
  const { selectedOrganization } = useContext(DashboardContext)
  const [success, setSuccess] = useState(false)
  const [showPreviousYear, setShowPreviousYear] = useState(false)
  const [showFormbanner, setShowFormbanner] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams('')
  const [activeStep, setActiveStep] = React.useState(searchParams.get('step'))
  const [showInfoText, setShowInfoText] = useState(false)
  const navigate = useNavigate()

  const numberArray: Array<number> = []

  const [lastReportedYear, setLastReportedYear] = useState([
    {
      numEmployees: 0,
      collectiveAgreement: '',
    },
  ])
  const [currentYearReported, setCurrentYearReported] = useState(true)
  const [reportedYears, setReportedYears] = useState(numberArray)
  const [hasReceivedData, setHasReceivedData] = useState(false)
  const [selectedYear, setSelectedYear] = useState(0)
  const downloadlink = '/2.%20Tillämpliga avtalsområden.docx'

  const [thisYearsData, setThisYearsData] = useState({
    year: new Date().getFullYear() - 1,
    data: [
      {
        numEmployees: 0,
        collectiveAgreement: '',
      },
    ],
  })
  const [filteredData, setFilteredData] = useState({
    current: {
      year: 0,
      data: [
        {
          numEmployees: 0,
          collectiveAgreement: '',
        },
      ],
    },
    previous: {
      year: 0,
      data: [
        {
          numEmployees: 0,
          collectiveAgreement: '',
        },
      ],
    },
  })

  const tableHeads = [
    {
      label: 'Kollektivavtal',
    },
    {
      label: 'Antal anställda',
    },
  ]

  const getCollectiveAgreement = async () => {
    try {
      const res = await api.get('/enums/collective-agreements')

      return res.data.data
    } catch (e) {
      return []
    }
  }

  const getAgreementData = async () => {
    try {
      setHasReceivedData(false)
      const res = await api.get(`/accounts/${selectedOrganization.id}/kpi`)
      setHasReceivedData(true)

      return res.data.data
    } catch (e) {
      return []
    }
  }

  const findYears = (data: Array<IAgreementData>) => {
    const tempArray: Array<number> = []
    for (const index in data) {
      if (!tempArray.includes(data[index].year)) {
        tempArray.push(data[index].year)
      }
    }
    if (!tempArray.includes(new Date().getFullYear() - 1)) {
      setCurrentYearReported(false)
      setShowFormbanner(true)
    } else {
      setCurrentYearReported(true)
      setShowPreviousYear(true)
      setShowFormbanner(false)
    }

    const sortedTempArray = tempArray.sort((a, b) => b - a)

    setReportedYears(sortedTempArray)
    setSelectedYear(sortedTempArray[0])
  }

  const getData = async () => {
    if (selectedOrganization && selectedOrganization.id) {
      const collectiveData = await getCollectiveAgreement()

      const customOrder = [
        'F-avtal Beting',
        'Byggavtalet',
        'S-avtalet',
        'K-avtalet',
        'I-avtalet',
        'F-avtalet',
      ]
      const collectiveDataInCustomOrder = {}

      Object.keys(collectiveData)
        .sort((a, b) => {
          return customOrder.indexOf(b) - customOrder.indexOf(a)
        })
        .forEach((key) => {
          collectiveDataInCustomOrder[key] = collectiveData[key]
        })

      setCollectiveAgreements(collectiveDataInCustomOrder)
      const thisYearsDataClone = Object.assign({}, thisYearsData)
      thisYearsDataClone.data = []
      Object.keys(collectiveData).forEach((collectiveDatakey) => {
        if (collectiveDatakey.length > 0) {
          thisYearsDataClone.data.push({
            collectiveAgreement: collectiveDatakey,
            numEmployees: 0,
          })
        }
      })

      setThisYearsData(thisYearsDataClone)
      const receivedData = await getAgreementData()

      const newLastReportedYear = receivedData.find(
        (d: { year: number }) => d.year === new Date().getFullYear() - 2,
      )

      if (newLastReportedYear && newLastReportedYear.data) {
        setLastReportedYear(newLastReportedYear.data)
      }
      setReceivedData(receivedData)
      findYears(receivedData)
    }
  }

  useEffect(() => {
    setReceivedData([])
    getData()
  }, [selectedOrganization])

  useEffect(() => {
    const currentYearDataIndex = receivedData.findIndex((element) => element.year === selectedYear)
    const previousYearDataIndex = receivedData.findIndex(
      (element) => element.year === selectedYear - 1,
    )
    const data: IFilteredData = {
      current: receivedData[currentYearDataIndex],
      previous: receivedData[previousYearDataIndex],
    }
    setFilteredData(data)
  }, [selectedYear])

  useEffect(() => {
    if (success) {
      searchParams.delete('step')
      setSearchParams({ step: '2' })
    }
  }, [success])

  return (
    <div className="card p-12 flex flex-col w-full bg-white gap-8">
      {showFormbanner && <StatisticsSubmitHeader label="Antal anställda" acitveStep={activeStep} />}
      {(!activeStep || activeStep === '1') && (
        <div className="flex flex-col items-center">
          <div className="flex flex-col w-full gap-4 mb-4">
            <h2>Antal anställda {new Date().getFullYear() - 1}</h2>
            <p className="md:pr-12">
              Rapportera in antal anställda, exklusive VD, på dina avtalsområden per den 31
              december.
            </p>
            <div className="flex flex-col w-full items-start gap-4">
              <button
                onClick={() => setShowInfoText(!showInfoText)}
                className="text-darkBlue md:pr-12 text-green font-bold flex flex-row gap-2 items-center"
              >
                Vad ska rapporteras?
                {showInfoText ? (
                  <FontAwesomeIcon icon={faChevronUp} />
                ) : (
                  <FontAwesomeIcon icon={faChevronDown} />
                )}
              </button>
              {showInfoText && (
                <div className="flex flex-col w-full items-start">
                  <p className="p-small">
                    Vänligen rapportera antalet personer som är anställda per den sista december.
                    Gäller även deltidsanställda men inte styrelsemedlemmar eller personer över 65
                    år.
                  </p>
                  <a
                    className="text-coral font-bold underline hover:font-bold text-md mt-2 flex flex-row gap-2 items-center"
                    href={downloadlink}
                  >
                    <FontAwesomeIcon icon={faArrowRight} className="text-coral" /> Se fastigos olika avtal <FontAwesomeIcon icon={faFileDownload} className="text-coral" />
                  </a>
                </div>
              )}
            </div>
          </div>
          {hasReceivedData ? (
            <div className="flex flex-col justify-between w-full">
              <div className="flex flex-col justify-between md:flex-row">
                {!currentYearReported && (
                  <AgreementForm
                    collectiveAgreements={collectiveAgreements}
                    lastReportedYear={lastReportedYear}
                    thisYearsData={thisYearsData}
                    setThisYearsData={setThisYearsData}
                    setCurrentYearReported={setCurrentYearReported}
                    setSuccess={setSuccess}
                  />
                )}
              </div>
              {reportedYears && reportedYears.length > 0 && (
                <AgreementsTable
                  showPreviousYear={showPreviousYear}
                  setShowPreviousYear={setShowPreviousYear}
                  reportedYears={reportedYears}
                  selectedYear={selectedYear}
                  setSelectedYear={setSelectedYear}
                  tableHeads={tableHeads}
                  filteredData={filteredData}
                  collectiveAgreements={collectiveAgreements}
                />
              )}
            </div>
          ) : (
            <div className="m-40">
              <Spinner />
            </div>
          )}
        </div>
      )}
      {activeStep === '2' && <BillingInfo fromForm setActiveStep={setActiveStep} />}
      {activeStep === '3' && (
        <div>
          <UsersPage fromForm />
          <button onClick={() => navigate('/')} className="btn">
            Uppgifterna stämmer
          </button>
        </div>
      )}
    </div>
  )
}

export default Agreement
